<template>
    <div class="forum-search panel">
        <div class="panel-body">
            <header>
                <h1>Forum Search</h1>
            </header>

            <div class="flex">
                <div class="flex-full">
                    <div class="search-text-controls">
                        <input
                            class="form-control"
                            type="text"
                            placeholder="Search forum posts and threads"
                            ref="searchInput"
                            @keyup.enter="$event.target.blur()"
                            v-model="searchText">
                        <button @click="reloadResults()">
                            <i class="fa fa-search"></i>
                        </button>
                    </div>

                    <div
                        class="search-restriction-warning"
                        v-if="hasSearchRestrictions()">
                        <div title="See advanced options">
                            {{ searchRestrictionWarning }}
                        </div>
                        <button
                            title="Clear restrictions"
                            @click="clearSearchRestrictions()">
                            <i class="fa fa-times"></i>
                        </button>
                    </div>

                    <div class="result-controls">
                        <TabSelect
                            :options="searchModeOptions"
                            v-model="searchMode">
                        </TabSelect>

                        <div class="sort-dropdown">
                            <div>Sort by:</div>
                            <DropdownSelect
                                :options="sortOptionsForPosts"
                                :value="dropdownSortForPosts"
                                @input="sortForPosts = $event"
                                v-show="showingPosts">
                            </DropdownSelect>
                            <DropdownSelect
                                :options="sortOptionsForThreads"
                                :value="dropdownSortForThreads"
                                @input="sortForThreads = $event"
                                v-show="showingThreads">
                            </DropdownSelect>
                        </div>
                    </div>

                    <div v-show="showingPosts">
                        <ForumPostResultList
                            ref="postList"
                            :posts="posts">
                        </ForumPostResultList>

                        <div
                            class="after-posts-msg"
                            v-if="showAfterPostsMessage">
                            That's all, folks!
                        </div>

                        <Loader v-if="loadingPosts">
                        </Loader>
                    </div>

                    <div v-show="showingThreads">
                        <ForumThreadList
                            ref="threadList"
                            :sortable="true"
                            :sortOrder="headerSortForThreads"
                            :threads="threads"
                            @update:sort-order="sortForThreads = $event">
                        </ForumThreadList>

                        <div
                            class="after-threads-msg"
                            v-if="showAfterThreadsMessage">
                            That's all, folks!
                        </div>

                        <Loader v-if="loadingThreads">
                        </Loader>
                    </div>
                </div>

                <div class="flex-none">
                    <ForumSearchAdvancedOptions
                        :allCategories="allCategories"
                        :allTermTypes="allTermTypes"
                        :allPaths="allPaths"
                        :allAssignments="allAssignments"
                        :selectedCategoryIds.sync="filters_categoryIds"
                        :selectedTermTypes.sync="filters_termTypes"
                        :selectedPathIds.sync="filters_pathIds"
                        :selectedAssignmentIds.sync="filters_assignmentIds">
                    </ForumSearchAdvancedOptions>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    // ForumSearch: the search hub for the forum. Allows a user to search for
    // posts or threads by text, and filter by content associations.

    import AssignmentService from '../services/assignments';
    import DropdownSelect from './reusable/forum/dropdown-select';
    import ForumPostResultList from './reusable/forum/post-result-list';
    import ForumSearchAdvancedOptions from './widgets/ForumSearchAdvancedOptions';
    import ForumService from '../services/forum';
    import ForumThreadList from './reusable/forum/thread-list';
    import ForumUtils from '../utils/forum';
    import Loader from './reusable/forum/loader';
    import PathService from '../services/paths';
    import TabSelect from './reusable/forum/tab-select';
    import TermService from '../services/terms';
    import TermUtils from '../utils/terms';


    function getViewportBottom() {
        const $window = $(window);
        return $window.scrollTop() + $window.height();
    }

    function getBottomPos(elem) {
        const $elem = $(elem);
        return $elem.offset().top + $elem.outerHeight();
    }

    /**
     * The paginated thread list from the forum utils uses getThreadsAndCount(),
     * but we need to get threads using searchThreads().
     */
    class PaginatedSearchThreadList extends ForumUtils.PaginatedList {
        constructor(search, params = {}) {
            const fetchFn = async (limit, offset) => {
                const resParams = Object.assign({}, params, { limit, offset });
                const res = await ForumService.searchThreads(search, resParams);
                return {
                    items: res.threads,
                    count: res.count,
                };
            };
            super(fetchFn);
        }

        get threads() {
            return this.items;
        }
    }

    /**
     * The paginated post list from the forum utils uses getPostsAndCount(), but
     * we need to get posts using searchPosts().
     */
    class PaginatedSearchPostList extends ForumUtils.PaginatedList {
        constructor(search, params = {}) {
            const fetchFn = async (limit, offset) => {
                const resParams = Object.assign({}, params, { limit, offset });
                const res = await ForumService.searchPosts(search, resParams);
                return {
                    items: res.posts,
                    count: res.count,
                };
            };
            super(fetchFn);
        }

        get posts() {
            return this.items;
        }
    }


    export default {
        components: {
            DropdownSelect,
            ForumPostResultList,
            ForumSearchAdvancedOptions,
            ForumThreadList,
            Loader,
            TabSelect,
        },

        data() {
            return {
                searchText: '',
                searchMode: 'posts',

                // The sort options for posts are fairly straight-forward since
                // the dropdown is the only control.
                sortOptionsForPosts: [
                    { value: 'searchRank:desc', label: 'Relevance' },
                    { value: 'createdAt:desc',  label: 'Date' },
                    { value: 'likeCount:desc',  label: 'Likes' },
                ],
                sortForPosts: 'default',

                // The sort options for threads, however, are a bit more
                // complicated, as they can be chosen through the dropdown or by
                // clicking on the thread list headers. We store the canonical
                // order in `sortForThreads`, and then use computed properties
                // to give to the ForumThreadList and the DropdownSelect.
                sortOptionsForThreads: [
                    { value: 'searchRank:desc',        label: 'Relevance' },
                    { value: 'createdAt:desc',         label: 'Date' },
                    { value: 'likeCount:desc',         label: 'Likes' },
                    { value: 'postCount:desc',         label: 'Replies' },
                    { value: 'viewCount:desc',         label: 'Views' },
                    { value: 'lastPostCreatedAt:desc', label: 'Activity' },
                ],
                sortForThreads: 'default',

                // The advanced options component requires a list of all the
                // options available. We'll store the resources in these lists
                // and use computed properties to handle any modifications.
                allAssignments: [],
                allCategories: [],
                allPaths: [],
                allTerms: [],

                filters_categoryIds: [],
                filters_termTypes: [],
                filters_pathIds: [],
                filters_assignmentIds: [],

                postList: new PaginatedSearchPostList(),
                threadList: new PaginatedSearchThreadList(),
            };
        },

        async created() {
            await this.loadFilterOptions();
        },

        async mounted() {
            this.loadFiltersFromUrl();
            this.syncFiltersToUrl();
            window.addEventListener('scroll', this.onScroll);
            window.addEventListener('resize', this.onResize);

            // Auto-focus the search field.
            this.$refs.searchInput.focus();

            // Reload the search results rather than auto loading since the
            // original lists are created without search parameters.
            await this.reloadResults();
        },

        beforeDestroy() {
            window.removeEventListener('scroll', this.onScroll);
            window.removeEventListener('resize', this.onResize);
        },

        methods: {
            onScroll() {
                if (this.searchMode === 'posts') {
                    this.autoLoadPosts();
                } else {
                    this.autoLoadThreads();
                }
            },

            onResize() {
                if (this.searchMode === 'posts') {
                    this.autoLoadPosts();
                } else {
                    this.autoLoadThreads();
                }
            },

            async loadFilterOptions() {
                const loadAssignments = async () => {
                    this.allAssignments = await AssignmentService.search();
                };
                const loadCategories = async () => {
                    await this.$store.dispatch('ensureForumCategories');
                    this.allCategories = this.$store.getters.forumCategories;
                };
                const loadPaths = async () => {
                    const allPaths = await PathService.getPaths();
                    this.allPaths = allPaths.filter(p => p.id != 9200) // filter out Houdini
                };
                const loadTerms = async () => {
                    this.allTerms = await TermService.get().then(response => {
                        if (!response.data.success) {
                            throw new Error(response.data.error);
                        }
                        return response.data.terms;
                    });
                };
                await Promise.all([
                    loadCategories(),
                    loadAssignments(),
                    loadTerms(),
                    loadPaths(),
                ]);
            },

            loadFiltersFromUrl() {
                function parseInts(str) {
                    return str
                        .split(',')
                        .map(v => parseInt(v))
                        .filter(v => !!v);
                }

                function parseStrings(str, whitelist = null) {
                    return str
                        .split(',')
                        .map(v => v.trim());
                }

                const query = this.$route.query;

                if (query.q) {
                    const value = query.q.toString().trim();
                    this.searchText = value;
                }

                if (query['post-sort']) {
                    const value = query['post-sort'].toString().trim();
                    this.sortForPosts = value;
                }

                if (query['thread-sort']) {
                    const value = query['thread-sort'].toString().trim();
                    this.sortForThreads = value;
                }

                if (query.category) {
                    const value = query.category.toString();
                    this.filters_categoryIds = parseInts(value);
                }

                if (query['term-type']) {
                    const value = query['term-type'].toString();
                    this.filters_termTypes = parseStrings(value);
                }

                if (query.path) {
                    const value = query.path.toString();
                    this.filters_pathIds = parseInts(value);
                }

                if (query.assignment) {
                    const value = query.assignment.toString();
                    this.filters_assignmentIds = parseInts(value);
                }
            },

            syncFiltersToUrl() {
                function intsToStr(ints) {
                    return ints
                        .map(i => i.toString())
                        .join(',');
                }

                const setQueryParam = (name, value) => {
                    // Need to clone the current query in order for router to
                    // detect change
                    const query = Object.assign({}, this.$route.query);
                    if (value === null) {
                        delete query[name];
                    } else {
                        query[name] = value;
                    }
                    this.$router.replace({ query });
                }

                this.$watch('searchText', function (value) {
                    value = value.trim();
                    if (value.length) {
                        setQueryParam('q', value);
                    } else {
                        setQueryParam('q', null);
                    }
                });

                this.$watch('sortForPosts', function (value) {
                    if (value !== 'default') {
                        setQueryParam('post-sort', value);
                    } else {
                        setQueryParam('post-sort', null);
                    }
                });

                this.$watch('sortForThreads', function (value) {
                    if (value !== 'default') {
                        setQueryParam('thread-sort', value);
                    } else {
                        setQueryParam('thread-sort', null);
                    }
                });

                this.$watch('filters_categoryIds', function (value) {
                    if (value.length) {
                        setQueryParam('category', intsToStr(value));
                    } else {
                        setQueryParam('category', null);
                    }
                });

                this.$watch('filters_termTypes', function (value) {
                    if (value.length) {
                        setQueryParam('term-type', value.join(','));
                    } else {
                        setQueryParam('term-type', null);
                    }
                });

                this.$watch('filters_pathIds', function (value) {
                    if (value.length) {
                        setQueryParam('path', intsToStr(value));
                    } else {
                        setQueryParam('path', null);
                    }
                });

                this.$watch('filters_assignmentIds', function (value) {
                    if (value.length) {
                        setQueryParam('assignment', intsToStr(value));
                    } else {
                        setQueryParam('assignment', null);
                    }
                });
            },

            clearSearchText() {
                this.searchText = '';
            },

            hasSearchRestrictions() {
                return (this.filters_categoryIds.length > 0 ||
                        this.filters_termTypes.length > 0 ||
                        this.filters_pathIds.length > 0 ||
                        this.filters_assignmentIds.length > 0);
            },

            clearSearchRestrictions() {
                this.filters_categoryIds = [];
                this.filters_termTypes = [];
                this.filters_pathIds = [];
                this.filters_assignmentIds = [];
            },

            isPostListBottomNearView() {
                const offset = 200;
                const viewBottom = getViewportBottom();
                const listBottom = getBottomPos(this.$refs.postList.$el);
                return listBottom <= viewBottom + offset;
            },

            shouldLoadMorePosts() {
                if (!this.canLoadMorePosts) {
                    // There is nothing else to load.
                    return false;

                } else if (this.loadingPosts) {
                    // We are already loading the next batch.
                    return false;

                } else if (!this.showingPosts) {
                    // If the posts aren't in view, we only need to load
                    // results to retrieve the total count.
                    return this.postList.totalCount === null;

                } else if (this.posts.length === 0) {
                    // If the post list was just reloaded, the bottom may still
                    // be out of view due to the old rendered posts; in this
                    // case, we want to fetch the new ones.
                    return true;
                }
                return this.isPostListBottomNearView();
            },

            async autoLoadPosts() {
                while (this.shouldLoadMorePosts()) {
                    await this.postList.loadMore();
                }
            },

            async reloadPostList() {
                this.postList = new PaginatedSearchPostList(
                    this.searchText,
                    this.postSearchParams,
                );
                await this.autoLoadPosts();
            },

            isThreadListBottomNearView() {
                const offset = 200;
                const viewBottom = getViewportBottom();
                const listBottom = getBottomPos(this.$refs.threadList.$el);
                return listBottom <= viewBottom + offset;
            },

            shouldLoadMoreThreads() {
                if (!this.canLoadMoreThreads) {
                    // There is nothing else to load.
                    return false;

                } else if (this.loadingThreads) {
                    // We are already loading the next batch.
                    return false;

                } else if (!this.showingThreads) {
                    // If the threads aren't in view, we only need to load
                    // results to retrieve the total count.
                    return this.threadList.totalCount === null;

                } else if (this.threads.length === 0) {
                    // If the thread list was just reloaded, the bottom may
                    // still be out of view due to the old rendered threads; in
                    // this case, we want to fetch the new ones.
                    return true;
                }
                return this.isThreadListBottomNearView();
            },

            async autoLoadThreads() {
                while (this.shouldLoadMoreThreads()) {
                    await this.threadList.loadMore();
                }
            },

            async reloadThreadList() {
                this.threadList = new PaginatedSearchThreadList(
                    this.searchText,
                    this.threadSearchParams,
                );
                await this.autoLoadThreads();
            },

            async reloadResults() {
                await Promise.all([
                    this.reloadPostList(),
                    this.reloadThreadList(),
                ]);
            },
        },

        computed: {
            dropdownSortForPosts() {
                if (this.sortForPosts === 'default') {
                    return this.sortOptionsForPosts[0].value;
                } else {
                    return this.sortForPosts;
                }
            },

            dropdownSortForThreads() {
                return {
                    'default':                'searchRank:desc',
                    'searchRank:desc':        'searchRank:desc',
                    'createdAt:desc':         'createdAt:desc',
                    'likeCount:desc':         'likeCount:desc',
                    'postCount:asc':          'postCount:desc',
                    'postCount:desc':         'postCount:desc',
                    'viewCount:asc':          'viewCount:desc',
                    'viewCount:desc':         'viewCount:desc',
                    'lastPostCreatedAt:asc':  'lastPostCreatedAt:desc',
                    'lastPostCreatedAt:desc': 'lastPostCreatedAt:desc',
                }[this.sortForThreads];
            },

            headerSortForThreads() {
                return {
                    'default':                'default',
                    'searchRank:desc':        'default',
                    'createdAt:desc':         'default',
                    'likeCount:desc':         'default',
                    'postCount:asc':          'postCount:asc',
                    'postCount:desc':         'postCount:desc',
                    'viewCount:asc':          'viewCount:asc',
                    'viewCount:desc':         'viewCount:desc',
                    'lastPostCreatedAt:asc':  'lastPostCreatedAt:asc',
                    'lastPostCreatedAt:desc': 'lastPostCreatedAt:desc',
                }[this.sortForThreads];
            },

            sharedSearchParams() {
                return {
                    categoryIds: this.filters_categoryIds,
                    termTypes: this.filters_termTypes,
                    pathIds: this.filters_pathIds,
                    assignmentIds: this.filters_assignmentIds,
                };
            },

            postSearchParams() {
                return Object.assign({}, this.sharedSearchParams, {
                    order: this.sortForPosts,
                });
            },

            threadSearchParams() {
                return Object.assign({}, this.sharedSearchParams, {
                    order: this.sortForThreads,
                });
            },

            threads() {
                return this.threadList.threads;
            },

            posts() {
                return this.postList.posts;
            },

            canLoadMorePosts() {
                return this.postList.hasMore();
            },

            canLoadMoreThreads() {
                return this.threadList.hasMore();
            },

            searchModeOptions() {
                let postsOption = {
                    value: 'posts',
                    label: `Posts`,
                };
                let threadsOption = {
                    value: 'threads',
                    label: `Threads`,
                };
                const postCount = this.postList.totalCount;
                if (postCount !== null) {
                    postsOption.label = `${postCount} Posts`;
                }
                const threadCount = this.threadList.totalCount;
                if (threadCount !== null) {
                    threadsOption.label = `${threadCount} Threads`;
                }
                return [postsOption, threadsOption];
            },

            loadingPosts() {
                return this.postList.isLoading();
            },

            loadingThreads() {
                return this.threadList.isLoading();
            },

            showingPosts() {
                return this.searchMode === 'posts';
            },

            showingThreads() {
                return this.searchMode === 'threads';
            },

            showAfterPostsMessage() {
                return !this.postList.hasMore();
            },

            showAfterThreadsMessage() {
                return !this.threadList.hasMore();
            },

            firstCategoryFilterLabel() {
                const catId = this.filters_categoryIds[0];
                if (!catId) {
                    return null;
                }
                const cat = this.allCategories.find(cat => cat.id === catId);
                if (!cat) {
                    return 'Loading';
                }
                return cat.label;
            },

            firstPathFilterName() {
                const pathId = this.filters_pathIds[0];
                if (!pathId) {
                    return null;
                }
                const path = this.allPaths.find(path => path.id === pathId);
                if (!path) {
                    return 'Loading';
                }
                return path.name;
            },

            firstAssignmentFilterName() {
                const aId = this.filters_assignmentIds[0];
                if (!aId) {
                    return null;
                }
                const a = this.allAssignments.find(a => a.id === aId);
                if (!a) {
                    return 'Loading';
                }
                return a.name;
            },

            /**
             * The search restriction informs the user of any restrictions that
             * are currently being applied to the search results. This is
             * helpful in cases where the user didn't deliberatly specify any
             * filters, such as when they click the search icon from a forum
             * category page.
             */
            searchRestrictionWarning() {
                function limitMsg(reason, more) {
                    const prefix = 'Note: search limited to';
                    const postfix = 'and more';
                    if (more) {
                        return `${prefix} ${reason} ${postfix}`;
                    } else {
                        return `${prefix} ${reason}`;
                    }
                }

                if (this.filters_categoryIds.length > 0) {
                    const label = this.firstCategoryFilterLabel;
                    const more = (this.filters_categoryIds.length > 1 ||
                                  this.filters_termTypes.length > 0 ||
                                  this.filters_pathIds.length > 0 ||
                                  this.filters_assignmentIds.length > 0);
                    return limitMsg(`${label} category`, more);

                } else if (this.filters_termTypes.length > 0) {
                    const tt = this.filters_termTypes[0];
                    const more = (this.filters_termTypes.length > 1 ||
                                  this.filters_pathIds.length > 0 ||
                                  this.filters_assignmentIds.length > 0);
                    return limitMsg(`${tt} terms`, more);

                } else if (this.filters_pathIds.length > 0) {
                    const name = this.firstPathFilterName;
                    const more = (this.filters_pathIds.length > 1 ||
                                  this.filters_assignmentIds.length > 0);
                    return limitMsg(`"${name}" path`, more);

                } else if (this.filters_assignmentIds.length > 0) {
                    // TODO: this has a minor bug in that selecting any
                    // assignment that shares a name with another will trigger
                    // `more` to be true; I'm leaving it until it becomes an
                    // issue.
                    const name = this.firstAssignmentFilterName;
                    const more = (this.filters_assignmentIds.length > 1);
                    return limitMsg(`"${name.trim()}" assignment`, more);
                }

                return '';
            },

            allTermTypes() {
                return TermUtils.getTypes(this.allTerms);
            },
        },

        watch: {
            searchText: function() {
                this.reloadResults();
            },

            postSearchParams: function() {
                this.reloadPostList();
            },

            threadSearchParams: function() {
                this.reloadThreadList();
            },
        },
    }
</script>

<style lang="scss" scoped>
    .flex {
        display: flex;
    }
    .flex-full {
        flex: 1;
    }
    .flex-none {
        flex: 0;
    }

    .forum-search .panel-body {
        padding: 40px;
        margin: 20px 0;
    }

    header {
        margin-bottom: 20px;

        h1 {
            margin: 0;
            font-size: 38px;
        }
    }

    .search-text-controls {
        position: relative;

        input {
            padding-right: 45px;
            border-color: #b0b0b0 !important;
            &:focus {
                border-color: #fff !important;
            }
        }

        button {
            background: none;
            border: none;
            position: absolute;
            top: 0;
            right: 0;
            width: 45px;
            height: 45px;

            &:hover {
                background: #363636;
            }

            i {
                font-size: 16px;
            }
        }
    }

    .search-restriction-warning {
        display: flex;
        margin: 10px 0;
        color: #999;
        font-size: 12px;
        line-height: 20px;

        button {
            background: none;
            border: none;
            margin: 0 0 0 3px;
            padding: 0;
            font-size: 14px;
            height: 20px;
            width: 20px;

            &:hover {
                background: #363636;
            }
        }
    }

    .result-controls {
        display: flex;
        margin: 15px 0 20px;

        .tab-select {
            flex: 1
        }

        .sort-dropdown {
            display: flex;
            align-items: center;
        }
    }

    .forum-post-result-list {
        margin-top: 30px;
    }

    .forum-thread-list {
        margin-top: 20px;
    }

    .after-posts-msg,
    .after-threads-msg {
        font-weight: bold;
        margin-top: 30px;
    }
</style>

<style lang="scss">
    .forum-search {
        .forum-dropdown-select.dropdown-select {
            margin-left: 20px;
        }

        .forum-thread-list {
            .thread-title {
                font-size: 14px;
            }
        }
    }
</style>



// WEBPACK FOOTER //
// ForumSearch.vue?0265e10f